/**
 * Dev overlay — live tunables. (§15.5)
 *
 * ── ⚠ READ THIS BEFORE BELIEVING A SLIDER ─────────────────────────────────
 *
 * constants.ts's own header says: "The dev overlay (leva) binds directly to
 * `TUNABLES` so feel can be iterated live without a rebuild." That is NOT
 * achievable as the code stands, and shipping a panel of sliders that silently
 * do nothing would be worse than shipping no panel.
 *
 * The reason: /sim imports the individual constants —
 *
 *     import { BALL_RESTITUTION_GRASS } from './constants.js';
 *
 * — not `TUNABLES.ball.restitution`. An ES module binding to a `const` is
 * immutable, so mutating the TUNABLES *object* cannot reach any of them.
 * TUNABLES is a MIRROR of the constants, not their source. (Verified by grep:
 * no file under /sim reads TUNABLES at all.)
 *
 * So the groups split in two, and the panel says which is which:
 *
 *   LIVE   camera.*  — render/camera.ts reads TUNABLES.camera every frame, so
 *                      these genuinely retune in real time. This is why that
 *                      file reads the object instead of importing the constants.
 *   INERT  ball.* player.* shooting.* — display-only. Moving them changes
 *                      nothing until /sim reads TUNABLES, which is a change to
 *                      frozen files and therefore not this agent's to make.
 *
 * TO FIX: have the sim read `TUNABLES.x.y` at the point of use instead of
 * importing the constant. It is mechanical but it touches every sim file, it
 * costs a property load in the hot path, and it must NOT be done to values the
 * server also reads without forcing the session offline first — which is the
 * next point.
 *
 * ── Why a tunable change forces an offline session ─────────────────────────
 *
 * The sim is a pure function of (state, inputs) and BOTH sides run it (§15.2).
 * Changing a constant on the client only makes the client a different game from
 * the server: every tick diverges, reconciliation fights every snapshot, and the
 * symptom (players juddering) points nowhere near the cause. So the panel is
 * dev-build only, and `onRequireOffline` fires before any sim-affecting group
 * can be touched. Camera values are pure presentation and are exempt.
 *
 * ── Why the whole module is dynamically imported ───────────────────────────
 *
 * leva pulls in React and a full DOM panel. A static import would put all of it
 * in the production bundle against §6.8's 25MB initial-load budget, for a panel
 * no player can open. `mountDevOverlay` returns null outside a dev build and
 * never touches the import.
 */

import { TUNABLES } from '@fg/shared/sim';

export interface DevOverlayOptions {
  /**
   * Fires the first time a SIM-affecting tunable is touched. The session must go
   * offline (or reconnect) — see the header. Camera edits never fire it.
   */
  onRequireOffline?: () => void;
  /** Live diagnostics, polled by the panel. Cheap — it reads at ~4Hz. */
  readStats?: () => DevStats;
}

export interface DevStats {
  fps: number;
  rttMs: number;
  /** Reconciliation: corrections/sec and the last error in metres. */
  correctionsPerSec: number;
  lastErrorM: number;
  replayedTicks: number;
  /** Interpolation mode name — 'Interpolated' | 'Extrapolated' | 'Frozen' | ... */
  interpMode: string;
  snapshotBuffer: number;
  predictedTick: number;
  serverTick: number;
  /** §10 camera debug panel — the live local camera state. */
  cameraName: string;
  /** Mouse-look yaw in degrees (the ONLY driver of the follow-cam heading). */
  cameraLookDeg: number;
  /**
   * True while a shot/pass is charging. It used to mean "the look yaw is frozen"
   * (§10's trajectory freeze); since 2026-07-25 the look keeps turning through a
   * charge — freezing it froze the AIM, now that the crosshair IS the aim — at
   * the finer `aimSensitivity` gain instead. Renamed rather than left saying
   * "frozen" about a camera that moves.
   */
  cameraCharging: boolean;
  cameraBlend: number;
  cameraFov: number;
  /**
   * The SEMANTIC animation command the controlled player is playing, straight
   * off the sim's `AnimState` (see render/player/animationCommand.ts). This is
   * the panel line that answers "the sim says he is passing — is he?", which
   * used to be unanswerable: the renderer derived its own state from velocity
   * and the two could disagree with nothing to show it.
   */
  animCommand: string;
  /**
   * Ball contacts raised by animations this session. Monotonic. A strike that
   * never increments this is a strike whose contact beat never fired; a strike
   * that increments it twice is a duplicate impulse.
   */
  animContacts: number;
}

export interface DevOverlay {
  dispose(): void;
}

/** True only in a Vite dev build. Production tree-shakes everything below. */
export function isDevBuild(): boolean {
  return import.meta.env.DEV;
}

/**
 * Mount the leva panel. Returns null in production, or if leva/React fail to
 * load — a dev tool must never be able to take the game down with it.
 */
export async function mountDevOverlay(opts: DevOverlayOptions = {}): Promise<DevOverlay | null> {
  if (!isDevBuild()) return null;

  try {
    const [{ createElement, useEffect, useState }, { createRoot }, leva] = await Promise.all([
      import('react'),
      import('react-dom/client'),
      import('leva'),
    ]);
    const { useControls, folder, Leva } = leva;

    const host = document.createElement('div');
    host.id = 'fg-devtools';
    document.body.appendChild(host);

    let warned = false;
    const warnOnce = (): void => {
      if (warned) return;
      warned = true;
      opts.onRequireOffline?.();
    };

    /**
     * The control panel. Every sim group is suffixed INERT so nobody spends an
     * afternoon wondering why the ball feels identical (see the header).
     */
    const Panel = (): ReturnType<typeof createElement> => {
      const [stats, setStats] = useState<DevStats | null>(null);

      useEffect(() => {
        if (!opts.readStats) return undefined;
        // 4Hz. This is a diagnostic readout; polling it per frame would make the
        // dev tool the thing that costs the frame budget it is measuring.
        const id = window.setInterval(() => setStats(opts.readStats!()), 250);
        return () => window.clearInterval(id);
      }, []);

      useControls({
        camera: folder({
          height: {
            value: TUNABLES.camera.height, min: 4, max: 45, step: 0.5,
            onChange: (v: number) => { TUNABLES.camera.height = v; },
          },
          distance: {
            value: TUNABLES.camera.distance, min: 5, max: 60, step: 0.5,
            onChange: (v: number) => { TUNABLES.camera.distance = v; },
          },
          fov: {
            value: TUNABLES.camera.fov, min: 30, max: 100, step: 1,
            onChange: (v: number) => { TUNABLES.camera.fov = v; },
          },
          smoothTime: {
            value: TUNABLES.camera.smoothTime, min: 0.02, max: 1.2, step: 0.01,
            onChange: (v: number) => { TUNABLES.camera.smoothTime = v; },
          },
          anticipation: {
            value: TUNABLES.camera.anticipation, min: 0, max: 1, step: 0.01,
            onChange: (v: number) => { TUNABLES.camera.anticipation = v; },
          },
        }),
        'ball (INERT — see overlay.ts)': folder(
          {
            restitution: {
              value: TUNABLES.ball.restitution, min: 0, max: 1, step: 0.01,
              onChange: (v: number) => { TUNABLES.ball.restitution = v; warnOnce(); },
            },
            frictionGrass: {
              value: TUNABLES.ball.frictionGrass, min: 0, max: 1, step: 0.01,
              onChange: (v: number) => { TUNABLES.ball.frictionGrass = v; warnOnce(); },
            },
            magnusCoeff: {
              value: TUNABLES.ball.magnusCoeff, min: 0, max: 1, step: 0.005,
              onChange: (v: number) => { TUNABLES.ball.magnusCoeff = v; warnOnce(); },
            },
            airDensity: {
              value: TUNABLES.ball.airDensity, min: 0.5, max: 1.5, step: 0.005,
              onChange: (v: number) => { TUNABLES.ball.airDensity = v; warnOnce(); },
            },
          },
          { collapsed: true },
        ),
        'player (INERT — see overlay.ts)': folder(
          {
            sprintSpeed: {
              value: TUNABLES.player.sprintSpeed, min: 3, max: 14, step: 0.1,
              onChange: (v: number) => { TUNABLES.player.sprintSpeed = v; warnOnce(); },
            },
            acceleration: {
              value: TUNABLES.player.acceleration, min: 1, max: 25, step: 0.1,
              onChange: (v: number) => { TUNABLES.player.acceleration = v; warnOnce(); },
            },
            turnRateMax: {
              value: TUNABLES.player.turnRateMax, min: 60, max: 720, step: 5,
              onChange: (v: number) => { TUNABLES.player.turnRateMax = v; warnOnce(); },
            },
            touchRadius: {
              value: TUNABLES.player.touchRadius, min: 0.1, max: 1.5, step: 0.01,
              onChange: (v: number) => { TUNABLES.player.touchRadius = v; warnOnce(); },
            },
          },
          { collapsed: true },
        ),
        'shooting (INERT — see overlay.ts)': folder(
          {
            minSpeed: {
              value: TUNABLES.shooting.minSpeed, min: 5, max: 30, step: 0.5,
              onChange: (v: number) => { TUNABLES.shooting.minSpeed = v; warnOnce(); },
            },
            maxSpeed: {
              value: TUNABLES.shooting.maxSpeed, min: 15, max: 55, step: 0.5,
              onChange: (v: number) => { TUNABLES.shooting.maxSpeed = v; warnOnce(); },
            },
            baseError: {
              value: TUNABLES.shooting.baseError, min: 0, max: 0.5, step: 0.005,
              onChange: (v: number) => { TUNABLES.shooting.baseError = v; warnOnce(); },
            },
          },
          { collapsed: true },
        ),
      });

      const line = (label: string, value: string): ReturnType<typeof createElement> =>
        createElement('div', { key: label, style: { display: 'flex', justifyContent: 'space-between', gap: '12px' } },
          createElement('span', { style: { opacity: 0.6 } }, label),
          createElement('span', null, value));

      return createElement(
        'div',
        null,
        createElement(Leva, { collapsed: false, titleBar: { title: 'FG dev (§15.5)' } }),
        stats
          ? createElement(
              'div',
              {
                style: {
                  position: 'fixed', right: '10px', top: '320px', width: '260px',
                  background: 'rgba(10,14,24,0.88)', color: '#fff', padding: '8px 10px',
                  font: '11px ui-monospace, monospace', borderRadius: '4px', zIndex: 1000,
                  pointerEvents: 'none',
                },
              },
              line('fps', stats.fps.toFixed(0)),
              line('rtt', stats.rttMs >= 0 ? `${stats.rttMs.toFixed(0)} ms` : 'n/a'),
              line('tick (pred/srv)', `${stats.predictedTick} / ${stats.serverTick}`),
              line('corrections/s', stats.correctionsPerSec.toFixed(1)),
              line('last error', `${stats.lastErrorM.toFixed(3)} m`),
              line('replayed', String(stats.replayedTicks)),
              line('interp', stats.interpMode),
              line('snap buffer', String(stats.snapshotBuffer)),
              line('camera', stats.cameraName),
              line('look yaw', `${stats.cameraLookDeg.toFixed(0)}°${stats.cameraCharging ? ' (aim gain)' : ''}`),
              line('off-ball', stats.cameraBlend.toFixed(2)),
              line('fov', `${stats.cameraFov.toFixed(0)}°`),
              line('anim', stats.animCommand),
              line('anim contacts', String(stats.animContacts)),
            )
          : null,
      );
    };

    const root = createRoot(host);
    root.render(createElement(Panel));

    return {
      dispose(): void {
        root.unmount();
        host.remove();
      },
    };
  } catch (err) {
    // A missing/incompatible leva must not stop the game booting.
    console.warn('[dev overlay] not mounted:', err);
    return null;
  }
}
